import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { forwardRef } from 'react';
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
} from 'react-router-dom';
import { useRouteRef, useRouteRefParams } from '@backstage/core-plugin-api';
import {
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSystemsCard,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_OWNER_OF,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { entityRouteRef } from '@backstage/plugin-catalog-react';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsLayout } from '../docs/TechDocsLayoutAddon';
import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
} from '../../theme/tokens';
import { EntityPageShell } from './entity-page/EntityPageShell';
import { EntityOverviewGrid } from './entity-page/EntityOverviewGrid';
import { EntityDependenciesRoute } from './entity-page/EntityDependenciesRoute';
import { EntityGroupOverviewCard } from './entity-page/EntityGroupOverviewCard';
import { EntityOpsGenieCard } from './entity-page/EntityOpsGenieCard';
import { EntityTeamLinksCard } from './entity-page/EntityTeamLinksCard';
import { EntityTeamCostCard } from './entity-page/EntityTeamCostCard';
import { EntityCostReportPanel } from './entity-page/EntityCostReportPanel';
import { hasVantageReport } from './entity-page/useVantageCost';
import { EntityDoraMetricsCard } from './entity-page/EntityDoraMetricsCard';
import { hasLinearBTeam } from './entity-page/useLinearBDora';
import { PrettyEntityApiDefinitionCard } from './PrettyEntityApiDefinitionCard';
import { useEntityPageModel } from './entity-page/useEntityPageModel';

type EntityRouteTabLinkProps = Omit<RouterLinkProps, 'to'> & {
  to?: RouterLinkProps['to'];
};

const EntityRouteTabLink = forwardRef<
  HTMLAnchorElement,
  EntityRouteTabLinkProps
>((props, ref) => {
  const { to, ...otherProps } = props;
  const entityRoute = useRouteRef(entityRouteRef);
  const routeParams = useRouteRefParams(entityRouteRef);
  const entityRoot = entityRoute(routeParams).replace(/\/$/, '');
  const routePath =
    typeof to === 'string' ? to.replace(/^\/+/, '').replace(/\/$/, '') : '';
  const resolvedTo = routePath ? `${entityRoot}/${routePath}` : entityRoot;

  return <RouterLink ref={ref} to={resolvedTo} {...otherProps} />;
});

EntityRouteTabLink.displayName = 'EntityRouteTabLink';

const entityRouteTabProps = {
  component: EntityRouteTabLink,
};

const hiddenEntityRouteTabProps = {
  ...entityRouteTabProps,
  style: { display: 'none' },
};

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <TechDocsLayout />
      <ReportIssue />
      <Mermaid
        lightConfig={{ theme: 'default' }}
        darkConfig={{ theme: 'dark' }}
      />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const hasOwnerOf = (entity: { relations?: { type: string }[] }) =>
  entity.relations?.some(r => r.type === RELATION_OWNER_OF) ?? false;

const hasTeamLinks = (entity: {
  metadata: { annotations?: Record<string, string> };
}) =>
  Boolean(
    entity.metadata.annotations?.['github.com/team-slug'] ||
    entity.metadata.annotations?.['slack.com/channel'],
  );

const hasOpsGenieTeam = (entity: {
  metadata: { annotations?: Record<string, string> };
}) => Boolean(entity.metadata.annotations?.['opsgenie.com/team']);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const entityWarningPanels = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <EntityOrphanWarning />
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <EntityProcessingErrorsPanel />
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const entityLinksExample = `metadata:
  links:
    - url: https://dashboard.example.com
      title: Dashboard
      icon: dashboard`;

const EntityLinksGridItem = () => {
  const model = useEntityPageModel();
  const links = model.links.metadata.all;

  if (links.length > 0) {
    return (
      <Grid item xs={12}>
        <EntityLinksCard cols={{ xs: 1, sm: 2, md: 3, lg: 4, xl: 4 }} />
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <Accordion
        disableGutters
        sx={theme => ({
          backgroundColor: getSurfaceColor(theme, 'raised'),
          backgroundImage: 'none',
          border: `1px solid ${getSurfaceBorder(theme)}`,
          borderRadius: `${radii.lg}px !important`,
          boxShadow: 'none',
          overflow: 'hidden',
          '&.MuiAccordion-root': {
            borderRadius: `${radii.lg}px !important`,
          },
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            borderRadius: `${radii.lg}px !important`,
            margin: 0,
          },
        })}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={theme => ({
            backgroundColor: getSurfaceColor(theme, 'raised'),
            minHeight: theme.spacing(7.75),
            padding: theme.spacing(0, 3),
            '&.Mui-expanded': {
              borderBottom: `1px solid ${getSurfaceBorder(theme)}`,
              minHeight: theme.spacing(7.75),
            },
            '& [class*="MuiAccordionSummary-content"]': {
              alignItems: 'center',
              margin: theme.spacing(1.75, 0),
              minWidth: 0,
            },
            '& [class*="MuiAccordionSummary-content"][class*="Mui-expanded"]': {
              margin: theme.spacing(1.75, 0),
            },
            '& [class*="MuiAccordionSummary-expandIconWrapper"]': {
              color: getThemeTokens(theme).textSecondary,
            },
            [theme.breakpoints.down('sm')]: {
              padding: theme.spacing(0, 2),
            },
          })}
        >
          <Box
            sx={theme => ({
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              gap: theme.spacing(1.5),
              minWidth: 0,
            })}
          >
            <Typography
              component="h2"
              sx={theme => ({
                color: getThemeTokens(theme).textPrimary,
                fontSize: theme.typography.h6.fontSize,
                fontWeight: 700,
                lineHeight: theme.typography.h6.lineHeight,
              })}
            >
              Links
            </Typography>
            <Typography
              component="span"
              sx={theme => ({
                alignItems: 'center',
                color: getThemeTokens(theme).textMuted,
                display: 'inline-flex',
                fontSize: theme.typography.body2.fontSize,
                lineHeight: theme.typography.body2.lineHeight,
              })}
            >
              No links configured
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails
          sx={theme => ({
            backgroundColor: getSurfaceColor(theme, 'raised'),
            padding: theme.spacing(2.5, 3, 3),
            [theme.breakpoints.down('sm')]: {
              padding: theme.spacing(2),
            },
          })}
        >
          <Typography
            sx={theme => ({
              color: getThemeTokens(theme).textSecondary,
              fontSize: theme.typography.body2.fontSize,
              lineHeight: theme.typography.body2.lineHeight,
              marginBottom: theme.spacing(2),
            })}
          >
            Add links to the entity YAML to surface dashboards, runbooks, or
            service docs here.
          </Typography>
          <Box
            component="pre"
            sx={theme => ({
              backgroundColor: getSurfaceColor(theme, 'subtle'),
              border: `1px solid ${getSurfaceBorder(theme)}`,
              borderRadius: `${radii.sm}px`,
              color: getThemeTokens(theme).textPrimary,
              fontFamily: 'Menlo, Monaco, Consolas, monospace',
              fontSize: theme.typography.body2.fontSize,
              lineHeight: theme.typography.body2.lineHeight,
              margin: 0,
              overflowX: 'auto',
              padding: theme.spacing(1.5, 2),
            })}
          >
            {entityLinksExample}
          </Box>
          <Button
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/descriptor-format#links-optional"
            rel="noopener noreferrer"
            sx={{ mt: 2 }}
            target="_blank"
            variant="outlined"
          >
            View descriptor docs
          </Button>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};

const overviewContent = (
  <Box sx={{ display: 'grid', gap: 3 }}>
    {entityWarningPanels}
    <EntityOverviewGrid />
  </Box>
);

const serviceEntityPage = (
  <EntityPageShell>
    <EntityPageShell.Route
      path="/"
      title="Overview"
      tabProps={entityRouteTabProps}
    >
      {overviewContent}
    </EntityPageShell.Route>

    <EntityPageShell.Route
      path="/api"
      title="API"
      tabProps={entityRouteTabProps}
    >
      <Box sx={{ display: 'grid', gap: 3 }}>
        <Box sx={{ minWidth: 0 }}>
          <EntityProvidedApisCard />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <EntityConsumedApisCard />
        </Box>
      </Box>
    </EntityPageShell.Route>

    <EntityPageShell.Route
      path="/dependencies"
      title="Dependencies"
      tabProps={entityRouteTabProps}
    >
      <EntityDependenciesRoute />
    </EntityPageShell.Route>

    <EntityPageShell.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
      tabProps={entityRouteTabProps}
    >
      <EntityKubernetesContent refreshIntervalMs={30000} />
    </EntityPageShell.Route>

    <EntityPageShell.Route
      path="/docs"
      title="Docs"
      tabProps={hiddenEntityRouteTabProps}
    >
      {techdocsContent}
    </EntityPageShell.Route>
  </EntityPageShell>
);

const websiteEntityPage = (
  <EntityPageShell>
    <EntityPageShell.Route
      path="/"
      title="Overview"
      tabProps={entityRouteTabProps}
    >
      {overviewContent}
    </EntityPageShell.Route>

    <EntityPageShell.Route
      path="/dependencies"
      title="Dependencies"
      tabProps={entityRouteTabProps}
    >
      <EntityDependenciesRoute />
    </EntityPageShell.Route>

    <EntityPageShell.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
      tabProps={entityRouteTabProps}
    >
      <EntityKubernetesContent refreshIntervalMs={30000} />
    </EntityPageShell.Route>

    <EntityPageShell.Route
      path="/docs"
      title="Docs"
      tabProps={hiddenEntityRouteTabProps}
    >
      {techdocsContent}
    </EntityPageShell.Route>
  </EntityPageShell>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityPageShell>
    <EntityPageShell.Route
      path="/"
      title="Overview"
      tabProps={entityRouteTabProps}
    >
      {overviewContent}
    </EntityPageShell.Route>

    <EntityPageShell.Route
      path="/docs"
      title="Docs"
      tabProps={hiddenEntityRouteTabProps}
    >
      {techdocsContent}
    </EntityPageShell.Route>
  </EntityPageShell>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityPageShell>
    <EntityPageShell.Route
      path="/"
      title="Overview"
      tabProps={entityRouteTabProps}
    >
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item xs={12} lg={7}>
          <EntityAboutCard />
        </Grid>
        <Grid item xs={12} lg={5}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <EntityLinksGridItem />
        <Grid item xs={12}>
          <EntityProvidingComponentsCard />
        </Grid>
        <Grid item xs={12}>
          <EntityConsumingComponentsCard />
        </Grid>
      </Grid>
    </EntityPageShell.Route>

    <EntityPageShell.Route
      path="/definition"
      title="Definition"
      tabProps={entityRouteTabProps}
    >
      <PrettyEntityApiDefinitionCard />
    </EntityPageShell.Route>
  </EntityPageShell>
);

const userPage = (
  <EntityPageShell>
    <EntityPageShell.Route
      path="/"
      title="Overview"
      tabProps={entityRouteTabProps}
    >
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
      </Grid>
    </EntityPageShell.Route>
  </EntityPageShell>
);

const groupPage = (
  <EntityPageShell>
    <EntityPageShell.Route
      path="/"
      title="Overview"
      tabProps={entityRouteTabProps}
    >
      <>
        {entityWarningContent}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
          }}
        >
          <EntityGroupOverviewCard />
          <EntitySwitch>
            <EntitySwitch.Case if={hasOwnerOf}>
              <EntityOwnershipCard />
            </EntitySwitch.Case>
          </EntitySwitch>
          <EntitySwitch>
            <EntitySwitch.Case if={hasVantageReport}>
              <EntityTeamCostCard />
            </EntitySwitch.Case>
          </EntitySwitch>
          <EntitySwitch>
            <EntitySwitch.Case if={hasLinearBTeam}>
              <EntityDoraMetricsCard />
            </EntitySwitch.Case>
          </EntitySwitch>
          <EntitySwitch>
            <EntitySwitch.Case if={hasOpsGenieTeam}>
              <EntityOpsGenieCard />
            </EntitySwitch.Case>
          </EntitySwitch>
          <EntitySwitch>
            <EntitySwitch.Case if={hasTeamLinks}>
              <EntityTeamLinksCard />
            </EntitySwitch.Case>
          </EntitySwitch>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <EntityMembersListCard />
          </Box>
        </Box>
      </>
    </EntityPageShell.Route>
    <EntityPageShell.Route
      path="/cost"
      title="Cost"
      if={hasVantageReport}
      tabProps={entityRouteTabProps}
    >
      <EntityCostReportPanel />
    </EntityPageShell.Route>
  </EntityPageShell>
);

const systemPage = (
  <EntityPageShell>
    <EntityPageShell.Route
      path="/"
      title="Overview"
      tabProps={entityRouteTabProps}
    >
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item xs={12} lg={7}>
          <EntityAboutCard />
        </Grid>
        <Grid item xs={12} lg={5}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <EntityLinksGridItem />
        <Grid item xs={12}>
          <EntityHasComponentsCard />
        </Grid>
        <Grid item xs={12} lg={6}>
          <EntityHasApisCard />
        </Grid>
        <Grid item xs={12} lg={6}>
          <EntityHasResourcesCard />
        </Grid>
      </Grid>
    </EntityPageShell.Route>
    <EntityPageShell.Route
      path="/diagram"
      title="Diagram"
      tabProps={entityRouteTabProps}
    >
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityPageShell.Route>
  </EntityPageShell>
);

const domainPage = (
  <EntityPageShell>
    <EntityPageShell.Route
      path="/"
      title="Overview"
      tabProps={entityRouteTabProps}
    >
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item xs={12} lg={7}>
          <EntityAboutCard />
        </Grid>
        <Grid item xs={12} lg={5}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <EntityHasSystemsCard />
        </Grid>
      </Grid>
    </EntityPageShell.Route>
  </EntityPageShell>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
